import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';
import { calculateEOLDate } from '../../utils/assetUtils';
import { LoadingSpinner } from '../../components/ui/LoadingState';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import KPICard from '../dashboard/components/KPICard';
import { exportToProfessionalCSV } from '../../utils/csvExport';
import SecurityAuditReport from '../reports/components/SecurityAuditReport';
import EOLBudgetReport from '../reports/components/EOLBudgetReport';

const ACTIVE_STATUSES = ['In Use', 'Available', 'In Repair', 'checked_out', 'in_storage'];
const DEAD_STATUSES = ['Broken', 'Stolen', 'Retired', 'Written-Off', 'Written Off', 'lost'];

const LifecycleDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';
  const [assets, setAssets] = useState([]);
  const [missingDataAssets, setMissingDataAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [forecastPeriod, setForecastPeriod] = useState('12_months');
  const [customStartDate, setCustomStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [customEndDate, setCustomEndDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]);

  // Fetch all assets and categorize them
  useEffect(() => {
    const fetchAssets = async () => {
      setIsLoading(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      try {
        const { data, error } = await supabase
          .from('assets')
          .select('asset_tag, product_name, category, purchase_date, lifespan_years, purchase_price, status');

        if (error) throw error;

        // Categorize assets
        const forecasted = [];
        const missing = [];

        data.forEach(asset => {
          // Skip dead assets for forecasting
          if (DEAD_STATUSES.includes(asset.status)) return;

          // Only process active statuses
          if (ACTIVE_STATUSES.includes(asset.status)) {
            const eolDate = calculateEOLDate(asset.purchase_date, asset.lifespan_years);

            if (!asset.purchase_date || !asset.lifespan_years || !eolDate) {
              missing.push(asset);
            } else {
              forecasted.push({
                ...asset,
                eolDate,
                calculatedExpiration: eolDate.toISOString(),
                isOverdue: eolDate < today
              });
            }
          }
        });

        // Sort forecasted assets by expiration date (soonest first)
        forecasted.sort((a, b) => a.eolDate - b.eolDate);

        setAssets(forecasted);
        setMissingDataAssets(missing);
        setAllAssets(data);
      } catch (error) {
        console.error('Error fetching assets:', error);
        addNotification(t('lifecycle.load_error'), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssets();
  }, []);

  const addNotification = (message, type = 'info') => {
    setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
  };

  // Split assets into Overdue and Future Forecast
  const { overdueAssets, futureForecastAssets } = useMemo(() => {
    const overdue = [];
    const future = [];

    assets.forEach(asset => {
      if (asset.isOverdue) overdue.push(asset);
      else future.push(asset);
    });

    return { overdueAssets: overdue, futureForecastAssets: future };
  }, [assets]);

  // Filter assets based on forecast period
  const expiringAssets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = new Date(today);
    let endDate = new Date(today);

    if (forecastPeriod === 'custom') {
      const [sYear, sMonth, sDay] = customStartDate.split('-').map(Number);
      const [eYear, eMonth, eDay] = customEndDate.split('-').map(Number);
      startDate = new Date(sYear, sMonth - 1, sDay);
      endDate = new Date(eYear, eMonth - 1, eDay);

      // For custom range, we strictly follow the range (might exclude overdue if range is in the future)
      return assets
        .filter(asset => asset.eolDate >= startDate && asset.eolDate <= endDate)
        .sort((a, b) => a.eolDate - b.eolDate);
    } else {
      if (forecastPeriod === '3_months') endDate.setMonth(today.getMonth() + 3);
      else if (forecastPeriod === '6_months') endDate.setMonth(today.getMonth() + 6);
      else if (forecastPeriod === '12_months') endDate.setMonth(today.getMonth() + 12);
      else if (forecastPeriod === '1_year') endDate.setFullYear(today.getFullYear() + 1);
      else if (forecastPeriod === '2_years') endDate.setFullYear(today.getFullYear() + 2);
      else if (forecastPeriod === '5_years') endDate.setFullYear(today.getFullYear() + 5);
      else if (forecastPeriod === 'all_expiring') endDate = new Date('2100-01-01');

      // For standard periods, we include EVERYTHING that is overdue + everything up to the end date
      return assets
        .filter(asset => asset.eolDate <= endDate)
        .sort((a, b) => a.eolDate - b.eolDate);
    }
  }, [assets, forecastPeriod, customStartDate, customEndDate]);

  // KPI Calculations
  const totalReplacementBudget = expiringAssets.reduce((sum, asset) => sum + (asset.purchase_price || 0), 0);
  const assetsToReplaceCount = expiringAssets.length;
  const criticalReplacementsCount = expiringAssets.filter(asset => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return asset.eolDate <= thirtyDaysFromNow;
  }).length;

  // Analytical Data: Budget by Category
  const budgetByCategory = useMemo(() => {
    const summary = expiringAssets.reduce((acc, asset) => {
      const cat = asset.category || 'Other';
      acc[cat] = (acc[cat] || 0) + (asset.purchase_price || 0);
      return acc;
    }, {});

    return Object.entries(summary)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [expiringAssets]);

  // Analytical Data: Monthly/Yearly Burn Rate
  const burnRateData = useMemo(() => {
    if (expiringAssets.length === 0) return [];

    // Determine if we should group by Year (range > 24 months)
    const firstDate = expiringAssets[0].eolDate;
    const lastDate = expiringAssets[expiringAssets.length - 1].eolDate;
    const diffMonths = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth());
    const useYearlyGrouping = diffMonths > 24;

    const summary = expiringAssets.reduce((acc, asset) => {
      if (!asset.eolDate) return acc;
      const key = useYearlyGrouping
        ? asset.eolDate.getFullYear().toString()
        : asset.eolDate.toLocaleString('default', { month: 'short', year: 'numeric' });

      acc[key] = (acc[key] || 0) + (asset.purchase_price || 0);
      return acc;
    }, {});

    return Object.entries(summary).map(([label, budget]) => ({ label, budget }));
  }, [expiringAssets]);

  // Export to CSV
  const handleExportCSV = () => {
    if (expiringAssets.length === 0) {
      addNotification(t('lifecycle.no_data_export'), 'warning');
      return;
    }

    exportToProfessionalCSV({
      data: expiringAssets,
      reportTitle: t('lifecycle.title'),
      appliedFilters: {
        ForecastPeriod: forecastPeriod,
        StartDate: forecastPeriod === 'custom' ? customStartDate : 'N/A',
        EndDate: forecastPeriod === 'custom' ? customEndDate : 'N/A',
      },
      fileNamePrefix: `lifecycle_forecast_${forecastPeriod}`,
      columns: [
        { key: 'asset_tag', label: t('labels.asset_tag') },
        { key: 'product_name', label: t('labels.product_name') },
        { key: 'category', label: t('labels.category') },
        { key: 'eolDate', label: t('lifecycle.eol_date'), value: (asset) => asset.eolDate?.toISOString().split('T')[0] || '' },
        { key: 'purchase_price', label: t('lifecycle.replacement_cost'), value: (asset) => asset.purchase_price?.toFixed(2) || '0.00' },
      ],
    });

    addNotification(t('lifecycle.export_success'), 'success');
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Missing Data Alert */}
      {missingDataAssets.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="p-2 bg-amber-100 rounded-lg h-fit text-amber-600">
            <Icon name="AlertTriangle" size={20} />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-amber-900">{t('lifecycle.missing_data_title')}</h3>
            <p className="text-xs text-amber-700 leading-relaxed">
              {t('lifecycle.missing_data_message', { count: missingDataAssets.length })}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {missingDataAssets.slice(0, 5).map(asset => (
                <span
                  key={asset.asset_tag}
                  className="px-2 py-0.5 bg-card/50 border border-amber-200 rounded text-[10px] font-medium text-amber-800 cursor-pointer hover:bg-white transition-colors"
                  onClick={() => navigate(`/assets/${asset.asset_tag}`)}
                >
                  {asset.asset_tag}
                </span>
              ))}
              {missingDataAssets.length > 5 && (
                <span className="text-[10px] font-medium text-amber-600 py-0.5">
                  {t('lifecycle.more_missing', { count: missingDataAssets.length - 5 })}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overdue Replacements Alert */}
      {overdueAssets.length > 0 && (
        <div className="bg-card border-l-4 border-red-500 rounded-xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 dark:border-red-600">
          <div className="p-6 bg-red-50/30 dark:bg-red-950/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg text-red-600 dark:bg-red-900/40 dark:text-red-400">
                <Icon name="AlertCircle" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-700 dark:text-red-400">{t('lifecycle.overdue_title')}</h2>
                <p className="text-sm text-red-600/80 dark:text-red-300/90 font-medium">
                  {t('lifecycle.overdue_message', { count: overdueAssets.length })}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto border border-red-100 dark:border-red-900/40 rounded-lg bg-card">
              <table className="w-full text-sm text-left">
                <thead className="bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 uppercase text-[10px] font-bold tracking-wider">
                  <tr>
                    <th className="px-4 py-3">{t('labels.asset_tag')}</th>
                    <th className="px-4 py-3">{t('labels.product_name')}</th>
                    <th className="px-4 py-3">{t('labels.category')}</th>
                    <th className="px-4 py-3 text-center">{t('lifecycle.expired_date')}</th>
                    <th className="px-4 py-3 text-right">{t('lifecycle.cost')}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-50 dark:divide-red-900/20">
                  {overdueAssets.slice(0, 5).map(asset => (
                    <tr key={asset.asset_tag} className="hover:bg-red-50/20 dark:hover:bg-red-950/20">
                      <td className="px-4 py-3 font-bold text-red-700 dark:text-red-400">{asset.asset_tag}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{asset.product_name}</td>
                      <td className="px-4 py-3 text-foreground/70">{asset.category}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-red-600 dark:text-red-400 font-bold">
                          {formatDate(asset.eolDate, preferredFormat)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-foreground">
                        {formatCurrency(asset.purchase_price)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                          onClick={() => navigate(`/assets/${asset.asset_tag}`)}
                        >
                          {t('buttons.view_details')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {overdueAssets.length > 5 && (
                    <tr>
                      <td colSpan="6" className="px-4 py-3 text-center text-xs font-medium text-red-600 dark:text-red-400 bg-red-50/50 dark:bg-red-950/20">
                        {t('lifecycle.more_overdue', { count: overdueAssets.length - 5 })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('lifecycle.title')}</h1>
          <p className="text-muted-foreground">{t('lifecycle.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {forecastPeriod === 'custom' && (
            <div className="flex flex-row items-center gap-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase whitespace-nowrap">{t('lifecycle.forecast_start')}</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-card border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ring/20 outline-none flex-1 min-w-0"
                />
              </div>
              <div className="flex-shrink-0">
                <span className="text-gray-300 text-xs font-bold px-1">{t('common.to') || 'to'}</span>
              </div>
              <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                <label className="text-[10px] font-bold text-muted-foreground/70 uppercase whitespace-nowrap">{t('lifecycle.forecast_end')}</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-card border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ring/20 outline-none flex-1 min-w-0"
                />
              </div>
            </div>
          )}
          <select
            value={forecastPeriod}
            onChange={(e) => setForecastPeriod(e.target.value)}
            className="bg-background border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
          >
            <option value="3_months">{t('lifecycle.next_3_months')}</option>
            <option value="6_months">{t('lifecycle.next_6_months')}</option>
            <option value="12_months">{t('lifecycle.next_12_months')}</option>
            <option value="1_year">{t('lifecycle.next_1_year')}</option>
            <option value="2_years">{t('lifecycle.next_2_years')}</option>
            <option value="5_years">{t('lifecycle.next_5_years')}</option>
            <option value="all_expiring">{t('lifecycle.all_expiring')}</option>
            <option value="custom">{t('lifecycle.custom_range')}</option>
          </select>
          <Button variant="outline" iconName="Download" onClick={handleExportCSV}>
            {t('lifecycle.export_csv')}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title={t('lifecycle.total_replacement_budget')}
          value={formatCurrency(totalReplacementBudget)}
          subtitle={forecastPeriod === 'custom' ? t('lifecycle.custom_range_label', { start: customStartDate, end: customEndDate }) : t('lifecycle.forecast_period_label', { period: forecastPeriod.replace('_', ' ') })}
          icon="TrendingUp"
          color="primary"
        />
        <KPICard
          title={t('lifecycle.assets_to_replace')}
          value={assetsToReplaceCount}
          subtitle={t('lifecycle.assets_to_replace_subtitle')}
          icon="Package"
          color="warning"
        />
        <KPICard
          title={t('lifecycle.critical_replacements')}
          value={criticalReplacementsCount}
          subtitle={t('lifecycle.critical_replacements_subtitle')}
          icon="AlertCircle"
          color="error"
        />
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget by Category */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <Icon name="PieChart" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">{t('lifecycle.budget_by_category')}</h3>
          </div>
          <div className="space-y-4">
            {budgetByCategory.length > 0 ? (
              budgetByCategory.map((item) => (
                <div key={item.name} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{item.name}</span>
                    <span className="text-muted-foreground">{formatCurrency(item.value)}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full"
                      style={{ width: `${totalReplacementBudget > 0 ? (item.value / totalReplacementBudget) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{t('lifecycle.no_category_data')}</p>
            )}
          </div>
        </div>

        {/* Forecasted Burn Rate */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">
              {burnRateData.length > 0 && burnRateData[0].label.length === 4 ? t('lifecycle.yearly_burn_rate') : t('lifecycle.monthly_burn_rate')}
            </h3>
          </div>
          <div className="space-y-4">
            {burnRateData.length > 0 ? (
              burnRateData.map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm font-medium">{item.label}</span>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-bold text-foreground">{formatCurrency(item.budget)}</div>
                    <div className="w-24 bg-muted rounded-full h-1.5 hidden sm:block">
                      <div
                        className="bg-accent h-1.5 rounded-full"
                        style={{ width: `${(item.budget / Math.max(...burnRateData.map(m => m.budget))) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{t('lifecycle.no_projections')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="List" size={20} className="text-primary" />
            {t('lifecycle.detailed_expiring_assets')}
          </h3>
          <span className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground font-medium">
            {t('lifecycle.assets_found', { count: expiringAssets.length })}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-muted/50 text-muted-foreground uppercase text-xs">
              <tr>
                <th className="px-6 py-4 font-medium">{t('labels.asset_tag')}</th>
                <th className="px-6 py-4 font-medium">{t('labels.product_name')}</th>
                <th className="px-6 py-4 font-medium">{t('labels.category')}</th>
                <th className="px-6 py-4 font-medium">{t('lifecycle.eol_date')}</th>
                <th className="px-6 py-4 font-medium text-right">{t('lifecycle.replacement_cost')}</th>
                <th className="px-6 py-4 font-medium text-center">{t('common.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {expiringAssets.length > 0 ? (
                expiringAssets.map((asset) => (
                  <tr key={asset.asset_tag} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-primary">{asset.asset_tag}</td>
                    <td className="px-6 py-4">{asset.product_name}</td>
                    <td className="px-6 py-4">{asset.category}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${asset.eolDate < new Date() ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                        }`}>
                        {formatDate(asset.eolDate, preferredFormat)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold">
                      {formatCurrency(asset.purchase_price)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => navigate(`/assets/${asset.asset_tag}`)}
                      >
                        {t('buttons.view_details')}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                    {t('lifecycle.no_matching_assets')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reports: Security Audit + EOL Budget Forecast */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SecurityAuditReport assets={allAssets} />
        <EOLBudgetReport assets={allAssets} />
      </div>

      <NotificationContainer
        notifications={notifications}
        onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
      />
    </div>
  );
};

export default LifecycleDashboard;
